import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { DayPlan, Exercise } from '../../../types/program';
import ExerciseEditor from './ExerciseEditor';

interface DayPlanEditorProps {
  dayPlan: DayPlan;
  dayIndex: number;
  onUpdate: (dayIndex: number, updatedDay: DayPlan) => void;
  onDelete: (dayIndex: number) => void;
}

const DayPlanEditor: React.FC<DayPlanEditorProps> = ({
  dayPlan,
  dayIndex,
  onUpdate,
  onDelete,
}) => {
  const handleDayNameChange = (name: string) => {
    onUpdate(dayIndex, { ...dayPlan, name });
  };

  const handleAddExercise = () => {
    const newExercise: Exercise = {
      name: '',
      description: '',
      videoUrl: '',
      notes: '',
      sets: 3,
      reps: '10',
      restTimeSec: 60,
      tempo: '2-0-2-0',
      required: true,
      exerciseType: 'strength',
      prescriptionType: 'reps',
      grouping: 'single',
      order: dayPlan.exercises.length,
      targets: {
        sets: 3,
        reps: 10,
        restSec: 60,
        tempo: '2-0-2-0',
      },
      media: {
        videoUrl: '',
      },
      coaching: {
        notes: '',
      },
    };

    onUpdate(dayIndex, {
      ...dayPlan,
      exercises: [...dayPlan.exercises, newExercise],
    });
  };

  const handleUpdateExercise = (exerciseIndex: number, updatedExercise: Exercise) => {
    const updatedExercises = [...dayPlan.exercises];
    updatedExercises[exerciseIndex] = updatedExercise;
    onUpdate(dayIndex, { ...dayPlan, exercises: updatedExercises });
  };

  const handleDeleteExercise = (exerciseIndex: number) => {
    const updatedExercises = dayPlan.exercises.filter((_, index) => index !== exerciseIndex);
    onUpdate(dayIndex, { ...dayPlan, exercises: updatedExercises });
  };

  return (
    <div className="bg-[#141414] border border-[#1F1F1F] rounded-xl p-5 px-6 mb-4">
      <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#1F1F1F]">
        <div className="flex items-center gap-4 flex-1">
          <div className="w-8 h-8 bg-[rgba(35,244,124,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-[#23F47C]">{dayPlan.dayNumber}</span>
          </div>
          <input
            type="text"
            value={dayPlan.name}
            onChange={(e) => handleDayNameChange(e.target.value)}
            placeholder={`Day ${dayPlan.dayNumber} Name`}
            className="flex-1 bg-transparent border-none text-white text-base font-semibold focus:outline-none placeholder-[#5F5F5F]"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onDelete(dayIndex)}
            className="w-9 h-9 bg-transparent border border-[#1F1F1F] rounded-lg flex items-center justify-center text-[#8F8F8F] hover:bg-[rgba(255,71,87,0.1)] hover:border-[#FF4757] hover:text-[#FF4757] transition-all duration-200"
            title="Delete Day"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {dayPlan.exercises.length === 0 ? (
          <div className="text-center py-8 text-[#8F8F8F] text-sm">
            No exercises added yet. Click "Add Exercise" to get started.
          </div>
        ) : (
          dayPlan.exercises.map((exercise, exerciseIndex) => (
            <ExerciseEditor
              key={exerciseIndex}
              exercise={exercise}
              exerciseIndex={exerciseIndex}
              onUpdate={handleUpdateExercise}
              onDelete={handleDeleteExercise}
            />
          ))
        )}
      </div>

      <button
        onClick={handleAddExercise}
        className="w-full h-12 mt-4 bg-transparent border-2 border-dashed border-[#1F1F1F] rounded-[10px] text-[#8F8F8F] text-sm font-semibold flex items-center justify-center gap-2 hover:border-[#23F47C] hover:text-[#23F47C] hover:bg-[rgba(35,244,124,0.05)] transition-all duration-200"
      >
        <Plus size={18} />
        <span>Add Exercise</span>
      </button>
    </div>
  );
};

export default DayPlanEditor;
